import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { SourceBadge } from "../components/SourceBadge";
import api from "../lib/api";
import { LEAD_STATUSES, statusLabel } from "../lib/data";
import { SOURCES, sourceByKey, aggregateBySource } from "../lib/sources";
import { LogOut, Building2, Users, ArrowRight, Filter, Download, Globe, Mail } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_source: {} });
  const [globalStats, setGlobalStats] = useState({ by_source: {} });
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | source key
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const statsParams = sourceFilter !== "all" ? { source_site: sourceFilter } : {};
    const leadsParams = {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(sourceFilter !== "all" ? { source_site: sourceFilter } : {}),
    };
    const [s, gs, l] = await Promise.all([
      api.get("/admin/leads/stats", { params: statsParams }),
      api.get("/admin/leads/stats"), // global, never filtered, for tabs counters
      api.get("/admin/leads", { params: leadsParams }),
    ]);
    setStats(s.data);
    setGlobalStats(gs.data);
    setLeads(l.data?.items || []);
    setLoading(false);
  };

  useEffect(() => { load(); document.title = "Dashboard Admin — Déménageur Pro"; }, [statusFilter, sourceFilter]);

  const bySource = aggregateBySource(globalStats.by_source || {});
  const grandTotal = Object.values(bySource).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#1C1C1C]">
        <div className="container mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="eyebrow bg-[#1D3557] text-white px-2 py-1">Admin</span>
            <h1 className="font-heading font-black text-xl tracking-tight">Dashboard CRM</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/sirene" className="text-sm font-semibold hover:text-[#2BD391] text-[#2C3E50]" data-testid="nav-admin-sirene">
              <Download className="w-4 h-4 inline mr-1.5" />Import Sirene
            </Link>
            <Link to="/admin/companies" className="text-sm font-semibold hover:text-[#2BD391] text-[#2C3E50]" data-testid="nav-admin-companies">
              <Building2 className="w-4 h-4 inline mr-1.5" />Entreprises
            </Link>
            <Link to="/admin/corrections" className="text-sm font-semibold hover:text-[#2BD391] text-[#2C3E50]" data-testid="nav-admin-corrections">
              <Mail className="w-4 h-4 inline mr-1.5" />Demandes
            </Link>
            <Link to="/admin/seo" className="text-sm font-semibold hover:text-[#2BD391] text-[#2C3E50]" data-testid="nav-admin-seo">
              <Globe className="w-4 h-4 inline mr-1.5" />SEO
            </Link>
            <span className="text-xs text-[#5C5C5C] hidden sm:inline">{user?.email}</span>
            <button onClick={logout} className="text-sm font-semibold hover:text-[#C44536]" data-testid="admin-logout">
              <LogOut className="w-4 h-4 inline mr-1" />Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-10">
        {/* Multi-brand source tabs */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">
            <Globe className="w-3.5 h-3.5" />
            CRM Multi-marques — leads par site source
          </div>
          <div className="flex flex-wrap gap-2" data-testid="source-tabs">
            <SourceTab
              active={sourceFilter === "all"}
              onClick={() => setSourceFilter("all")}
              label="Tous les sites"
              count={grandTotal}
              color="#1C1C1C"
              bg="#FFFFFF"
              testid="source-tab-all"
            />
            {SOURCES.map((s) => (
              <SourceTab
                key={s.key}
                active={sourceFilter === s.key}
                onClick={() => setSourceFilter(s.key)}
                label={s.label}
                count={bySource[s.key] || 0}
                color={s.color}
                bg={s.bg}
                testid={`source-tab-${s.key}`}
              />
            ))}
          </div>
        </div>

        <Link to="/admin/sirene" className="block mb-8 bg-gradient-to-br from-[#E8FBF3] to-white border border-[#2BD391] rounded-2xl p-5 hover:shadow-[0_12px_32px_-8px_rgba(43,211,145,0.25)] transition-all" data-testid="sirene-cta-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#2BD391] rounded-full flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-[#2C3E50]">Importer des entreprises depuis l&apos;INSEE Sirene</div>
              <div className="text-sm text-[#6B7280]">Recherchez les déménageurs officiels (NAF 49.42Z) par ville, code postal ou SIRET. 100% gratuit, sans clé API.</div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#2BD391]" />
          </div>
        </Link>

        {/* Status stats — react to active source filter */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard
            label={sourceFilter === "all" ? "Total demandes" : `Total ${sourceByKey(sourceFilter).label}`}
            value={stats.total}
            active={statusFilter === ""}
            onClick={() => setStatusFilter("")}
            testid="stat-total"
          />
          {LEAD_STATUSES.map((s) => (
            <StatCard
              key={s.key}
              label={s.label}
              value={stats.by_status?.[s.key] || 0}
              color={s.color}
              active={statusFilter === s.key}
              onClick={() => setStatusFilter(statusFilter === s.key ? "" : s.key)}
              testid={`stat-${s.key}`}
            />
          ))}
        </div>

        <div className="bg-white border border-[#1C1C1C] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 flex-wrap">
              <Users className="w-4 h-4" />
              Demandes de devis
              {sourceFilter !== "all" && <SourceBadge source={sourceFilter} size="md" />}
              {statusFilter && <span className="eyebrow ml-1 bg-[#F2EFE9] px-2 py-0.5">{statusLabel(statusFilter)}</span>}
            </h2>
            <button onClick={load} className="text-xs uppercase tracking-widest text-[#5C5C5C] hover:text-[#E07A5F]" data-testid="refresh-leads">
              <Filter className="w-3.5 h-3.5 inline mr-1" />Actualiser
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-[#F2EFE9]">
                <tr className="text-left">
                  <Th>Date</Th>
                  <Th>Source</Th>
                  <Th>Nom</Th>
                  <Th>Trajet</Th>
                  <Th>Volume</Th>
                  <Th>Date dém.</Th>
                  <Th>Statut</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-[#5C5C5C]">Chargement…</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-[#5C5C5C]" data-testid="no-leads">Aucune demande pour ces filtres.</td></tr>
                ) : leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#E5E0D8] hover:bg-[#FAF9F6]" data-testid={`lead-row-${l.id}`}>
                    <Td className="text-xs text-[#5C5C5C] whitespace-nowrap">{formatDate(l.created_at)}</Td>
                    <Td><SourceBadge source={l.source_site} size="xs" /></Td>
                    <Td className="font-semibold">{l.first_name} {l.last_name}<div className="text-xs text-[#5C5C5C] font-normal">{l.email}</div></Td>
                    <Td className="text-xs">
                      <div>{l.departure_city || "—"}</div>
                      <div className="text-[#5C5C5C]">→ {l.arrival_city || "—"}</div>
                    </Td>
                    <Td>{l.volume}</Td>
                    <Td>{l.moving_date}</Td>
                    <Td><StatusBadge status={l.status} /></Td>
                    <Td>
                      <div className="flex items-center gap-3">
                        <Link to={`/admin/leads/${l.id}`} className="text-xs uppercase tracking-widest hover:text-[#E07A5F]" data-testid={`view-lead-${l.id}`}>
                          Voir <ArrowRight className="w-3 h-3 inline" />
                        </Link>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const SourceTab = ({ active, onClick, label, count, color, bg, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
      active
        ? "border-[#1C1C1C] shadow-hard-sm -translate-y-0.5"
        : "border-[#E5E0D8] hover:border-[#1C1C1C]"
    }`}
    style={active ? { backgroundColor: bg } : { backgroundColor: "white" }}
  >
    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    <span>{label}</span>
    <span className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full ${active ? "bg-white/70" : "bg-[#F2EFE9]"}`}>{count}</span>
  </button>
);

const StatCard = ({ label, value, color = "#1C1C1C", active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-5 border transition-all ${active ? "border-[#1C1C1C] bg-white shadow-hard-sm -translate-y-0.5" : "border-[#E5E0D8] bg-white hover:border-[#1C1C1C]"}`}
  >
    <div className="text-3xl font-heading font-black" style={{ color }}>{value}</div>
    <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mt-1">{label}</div>
  </button>
);

const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
